import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  InlineLoading,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
  Stack,
  Tile,
} from "@carbon/react";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import { useNotification } from "../../context";
import {
  getImportErrorMessage,
  useConfirmImport,
  useDiscardImport,
  useImportFormats,
  useImportJob,
  useImportOptions,
  useImportSession,
  useProbeImport,
  useReplaceImportMapping,
  useResolveImportValues,
  useStartImport,
  useStartSheetImport,
} from "../../hooks/usePurchaseRequestImport";
import type {
  ImportMappingDTO,
  ImportProbeResponse,
  ImportResolveType,
} from "../../types";
import UploadStep from "./UploadStep";
import MappingStep from "./MappingStep";
import PreviewStep from "./PreviewStep";
import ImportJobStatus from "./ImportJobStatus";
import ConfirmImportPanel from "./ConfirmImportPanel";
import "./purchaseRequestImport.scss";

type Option = { id: string; label: string };

/**
 * Turning a spreadsheet of requisitions into purchase requests.
 *
 * Three steps, and the middle one is the point: a model proposes how to read the file, and the
 * buyer checks that proposal against their own data before anything exists. Nothing is created
 * until the last step, so opening the wrong file costs nothing.
 */
const PurchaseRequestImport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openNotification } = useNotification();

  /**
   * The import lives in the URL, not just in this component.
   *
   * Working out how to read a file takes a minute or two. Keeping the id only in state means a
   * refresh — or a colleague being sent the link — throws away a seven-hundred-row upload that is
   * sitting on the server perfectly intact.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session");
  const setSessionId = (id: string) =>
    setSearchParams({ session: id }, { replace: true });

  const [jobId, setJobId] = useState<string | null>(null);
  const [formatName, setFormatName] = useState("");
  const [formatNameTouched, setFormatNameTouched] = useState(false);
  const [skipAlreadyImported, setSkipAlreadyImported] = useState(true);

  /** What the file turned out to be, from the look taken the moment it was chosen. */
  const [probe, setProbe] = useState<ImportProbeResponse | null>(null);

  const fail = (error: unknown) =>
    openNotification(
      getImportErrorMessage(error) || t("purchaseRequests.import.error")
    );

  const { data: formats } = useImportFormats();
  const { data: session, isLoading } = useImportSession(sessionId);
  const { data: job } = useImportJob(jobId);

  const { mutate: probeFile, isPending: isProbing } = useProbeImport(
    (result) => setProbe(result),
    // A probe that fails costs nothing: the file is uploaded as if it were new, and the backend
    // matches it on the headers anyway. Not worth an error in front of somebody.
    () => setProbe(null)
  );

  const { mutate: startImport, isPending: isStarting } = useStartImport(
    (created) => setSessionId(created.id),
    fail
  );
  const { mutate: startSheetImport, isPending: isStartingSheet } =
    useStartSheetImport((created) => setSessionId(created.id), fail);
  const { mutate: replaceMapping, isPending: isMapping } =
    useReplaceImportMapping(sessionId, fail);
  const { mutate: resolveValues, isPending: isResolving } =
    useResolveImportValues(sessionId, fail);
  const { mutate: confirmImport, isPending: isConfirming } = useConfirmImport(
    sessionId,
    (started) => setJobId(started.id),
    fail
  );
  const { mutate: discardImport } = useDiscardImport(() =>
    navigate("/purchase-requests")
  );

  const { data: choices } = useImportOptions();

  /**
   * What the buyer can choose from when a value could not be matched.
   *
   * Keyed by the kind of thing the backend says needs matching, so the preview screen does not
   * have to know which list belongs to which column.
   */
  const options = useMemo<Record<ImportResolveType | "NONE", Option[]>>(
    () => ({
      USER: choices?.users ?? [],
      SITE: choices?.sites ?? [],
      NONE: [],
    }),
    [choices]
  );

  /**
   * What the format will be called, unless the buyer says otherwise.
   *
   * Importing always saves the format now, so this box is a name rather than a decision. It fills
   * itself in from the format the file matched, or from the file's own name — the point being
   * that nobody has to type anything for next month's upload to be free.
   */
  const suggestedFormatName =
    session?.formatName ??
    probe?.match?.name ??
    (session?.sourceName ?? "").replace(/\.[^.]+$/, "");
  const effectiveFormatName = formatNameTouched
    ? formatName
    : suggestedFormatName;

  const preview = session?.preview ?? null;
  const analysing = session?.status === "ANALYZING";
  const busy = isMapping || isResolving || isConfirming;

  // Mapping and preview always arrive together, so there is no separate "mapping" step to sit in.
  const currentStep = !sessionId ? 1 : jobId ? 3 : 2;

  const stepLabels = [
    t("purchaseRequests.import.stepFile"),
    t("purchaseRequests.import.stepReview"),
    t("purchaseRequests.import.stepImport"),
  ];

  const onConfirm = () => {
    if (!preview) return;
    confirmImport({
      previewHash: session?.previewHash ?? null,
      skipIdentities: skipAlreadyImported
        ? preview.historyDuplicates.map((duplicate) => duplicate.identityValue)
        : [],
      saveFormatAs: effectiveFormatName.trim() || null,
    });
  };

  // ---- the import is running or has finished -------------------------------

  if (jobId) {
    return <ImportJobStatus job={job} />;
  }

  return (
    <div>
      <PageHeader
        title={t("purchaseRequests.import.title")}
        action={
          sessionId ? (
            <Button kind="secondary" onClick={() => discardImport(sessionId)}>
              {t("purchaseRequests.import.discard")}
            </Button>
          ) : undefined
        }
      />

      <ProgressIndicator
        currentIndex={currentStep - 1}
        spaceEqually
        className="pr-import__steps"
      >
        {stepLabels.map((label) => (
          <ProgressStep key={label} label={label} />
        ))}
      </ProgressIndicator>

      {!sessionId && (
        <UploadStep
          formats={formats ?? []}
          isStarting={isStarting || isStartingSheet}
          isProbing={isProbing}
          probe={probe}
          onFileChosen={(file) => {
            setProbe(null);
            if (file) probeFile({ file });
          }}
          onStart={(file, description) => startImport({ file, description })}
          onStartSheet={(spreadsheetUrl, sheetName, description) =>
            startSheetImport({ spreadsheetUrl, sheetName, description })
          }
        />
      )}

      {sessionId && (isLoading || analysing) && (
        <Tile className="pr-import__panel">
          <InlineLoading description={t("purchaseRequests.import.working")} />
        </Tile>
      )}

      {session?.status === "FAILED" && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          className="pr-import__notification"
          title={t("purchaseRequests.import.analysisFailed")}
          subtitle={session.errorMessage ?? ""}
        />
      )}

      {session?.status === "READY_TO_REVIEW" && session.mapping && (
        <Stack gap={5}>
          {session.usedSavedFormat && (
            <InlineNotification
              kind="success"
              lowContrast
              hideCloseButton
              className="pr-import__notification"
              title={t("purchaseRequests.import.usedFormat", {
                name: session.formatName,
              })}
            />
          )}

          <MappingStep
            mapping={session.mapping}
            preview={preview}
            disabled={busy}
            onChange={(mapping: ImportMappingDTO) => replaceMapping(mapping)}
          />

          {preview && (
            <PreviewStep
              preview={preview}
              options={options}
              isResolving={isResolving}
              onResolve={(resolutions) => resolveValues(resolutions)}
            />
          )}

          <ConfirmImportPanel
            preview={preview}
            hasSavedFormat={!!session.formatName}
            formatName={effectiveFormatName}
            onFormatNameChange={(name) => {
              setFormatName(name);
              setFormatNameTouched(true);
            }}
            skipAlreadyImported={skipAlreadyImported}
            onSkipAlreadyImportedChange={setSkipAlreadyImported}
            busy={busy}
            isConfirming={isConfirming}
            onCancel={() => sessionId && discardImport(sessionId)}
            onConfirm={onConfirm}
          />
        </Stack>
      )}
    </div>
  );
};

export default PurchaseRequestImport;
